import React, { useEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";
import campaignLogoMobile from "./img/property-campaign-logo-mobile.png";
import campaignLogoDesktop from "./img/property-campaign-logo-desktop.png";
import "./PropertyCampaign.css";

const LANDING_PAGE_LEAD_ENDPOINT =
  "https://www.smart-lend.com/api/landing-page-leads/upsert";
const PROPERTY_CAMPAIGN_OTP_SEND_ENDPOINT =
  "https://www.smart-lend.com/api/landing-page-leads/property-campaign/otp/send";
const PROPERTY_CAMPAIGN_OTP_VERIFY_ENDPOINT =
  "https://www.smart-lend.com/api/landing-page-leads/property-campaign/otp/verify";
const LANDING_PAGE_PATH = "/property-campaign";
const LANDING_PAGE_VISITOR_COOKIE = "landing_page_visitor_token";
const LANDING_PAGE_SOURCE_COOKIE = "landing_page_source";
const PROPERTY_CAMPAIGN_COOKIE = "property_campaign_data";
const PROPERTY_VALUATION_ENDPOINT =
  "https://www.smart-lend.com/api/property/getPropertyValuation";

const LTV_BY_TYPE = {
  condo: 0.85,
  landed: 0.9,
  commercial: 0.75,
  industrial: 0.75,
  hdb: 0,
};

const TYPE_NAME = {
  condo: "Condo / Apt",
  landed: "Landed",
  commercial: "Commercial",
  industrial: "Industrial",
  hdb: "HDB",
};

const API_TYPE_CODES = ["land", "condo", "office"];
const CAMPAIGN_TYPE_TO_API_CODES = {
  condo: ["condo"],
  landed: ["land"],
  commercial: ["office"],
  industrial: ["office"],
};
const API_TYPE_TO_CAMPAIGN_TYPE = {
  condo: "condo",
  land: "landed",
  office: "commercial",
  hdb: "hdb",
};

const PRODUCTS = {
  caveat: {
    rate: 0.015,
    rateLabel: "1.5%/mth",
    name: "Caveat Backed Loan",
    cap: 12,
    unit: "months",
    defaultTenor: 12,
    tenorLabel: "Preferred tenor (months)",
    tenorHint: "Caveat backed loans run up to 12 months.",
  },
  firstlm: {
    rate: 0.06 / 12,
    rateLabel: "6%/yr",
    name: "1st Legal Mortgage",
    cap: 20,
    unit: "years",
    defaultTenor: 20,
    tenorLabel: "Preferred tenor (years)",
    tenorHint: "1st legal mortgages support tenors of up to 20 years.",
  },
};

const MIN_LOAN = 50000;
const CCR_DISTRICTS = [1, 2, 4, 6, 7, 9, 10, 11];
const RCR_DISTRICTS = [3, 5, 8, 12, 13, 14, 15];
const REGION_NAME = {
  ccr: "Core Central",
  rcr: "City Fringe",
  ocr: "Outside Central",
};

const propertyTabs = [
  { key: "condo", label: "Condo / Apt", icon: "C" },
  { key: "landed", label: "Landed", icon: "L" },
  { key: "commercial", label: "Commercial", icon: "S" },
  { key: "industrial", label: "Industrial", icon: "I" },
];

const productTabs = [
  {
    key: "caveat",
    label: "Caveat Backed Loan",
    note: "2nd charge - fast bridge - 24-48 hr approval",
    icon: "2",
  },
  {
    key: "firstlm",
    label: "1st Legal Mortgage",
    note: "1st charge - larger quantum - lower rate",
    icon: "1",
  },
];

const formatCurrency = (value) =>
  `S$${Math.round(Number(value) || 0).toLocaleString("en-SG")}`;

const parseAmount = (value) => {
  const numeric = Number(String(value || "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
};

const normalizeUnitPart = (value) => {
  const cleaned = String(value || "")
    .trim()
    .replace(/^#/, "")
    .replace(/\s+/g, "")
    .toUpperCase();

  if (!cleaned) return "";

  const withoutLeadingZeroes = cleaned.replace(/^0+([A-Z0-9])/, "$1");
  return withoutLeadingZeroes || "0";
};

const normalizeAddressUnit = (value) => {
  const cleaned = String(value || "")
    .trim()
    .replace(/^#/, "")
    .replace(/\s+/g, "")
    .toUpperCase();

  if (!cleaned) return "";

  return cleaned.split("-").map(normalizeUnitPart).filter(Boolean).join("-");
};

const getFloorUnit = (floor, unit) => {
  if (!unit && String(floor || "").includes("-")) {
    return normalizeAddressUnit(floor);
  }

  if (!floor && String(unit || "").includes("-")) {
    return normalizeAddressUnit(unit);
  }

  const cleanFloor = normalizeUnitPart(floor);
  const cleanUnit = normalizeUnitPart(unit);
  return cleanFloor && cleanUnit ? `${cleanFloor}-${cleanUnit}` : "";
};

const formatValuationAddress = (candidate) =>
  [
    candidate?.addressUnit,
    candidate?.addressNumber,
    candidate?.addressStreet,
    candidate?.postalCode,
    candidate?.country,
  ]
    .filter(Boolean)
    .join(", ");

const toValuationResult = (candidate, selectedPropertyType) => {
  const apiType = candidate?.propertyTypeCode;
  const propertyType =
    apiType === "office"
      ? selectedPropertyType
      : API_TYPE_TO_CAMPAIGN_TYPE[apiType] || selectedPropertyType;

  return {
    value: Number(candidate?.saleValuation) || 0,
    propertyType,
    apiType,
    addressUnit: candidate?.addressUnit || "",
    address: formatValuationAddress(candidate),
    raw: candidate,
  };
};

const getValuationCandidates = async (postalCode) => {
  const requests = API_TYPE_CODES.map(async (propertyTypeCode) => {
    const response = await fetch(PROPERTY_VALUATION_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        propertyTypeCode,
        postalCode,
        addressUnit: "",
        addressNumber: "",
        addressStreet: "",
        valuationType: "sale",
        projectName: "",
      }),
    });

    const json = await response.json();
    return Array.isArray(json?.data) ? json.data : [];
  });

  const results = await Promise.all(requests);
  return results.flat();
};

const narrowValuationCandidates = ({
  candidates,
  floorUnit,
  selectedPropertyType,
}) => {
  const apiCodes = CAMPAIGN_TYPE_TO_API_CODES[selectedPropertyType] || [];
  const typedCandidates = apiCodes.length
    ? candidates.filter((candidate) =>
        apiCodes.includes(candidate?.propertyTypeCode),
      )
    : candidates;

  if (!typedCandidates.length) {
    return {
      result: null,
      reason: "none_for_type",
      count: candidates.length,
      typedCount: 0,
    };
  }

  if (typedCandidates.length === 1) {
    return {
      result: toValuationResult(typedCandidates[0], selectedPropertyType),
      reason: "single",
      count: candidates.length,
      typedCount: typedCandidates.length,
    };
  }

  if (floorUnit) {
    const matchingResults = typedCandidates.filter(
      (candidate) => normalizeAddressUnit(candidate?.addressUnit) === floorUnit,
    );

    if (matchingResults.length === 1) {
      return {
        result: toValuationResult(matchingResults[0], selectedPropertyType),
        reason: "unit_match",
        count: candidates.length,
        typedCount: typedCandidates.length,
      };
    }

    return {
      result: null,
      reason:
        matchingResults.length > 1 ? "multiple_unit_matches" : "no_unit_match",
      count: candidates.length,
      typedCount: typedCandidates.length,
      matchCount: matchingResults.length,
    };
  }

  return {
    result: null,
    reason: "needs_unit",
    count: candidates.length,
    typedCount: typedCandidates.length,
  };
};

const createVisitorToken = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `lpl-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const districtOf = (postalCode) => {
  if (!/^\d{6}$/.test(postalCode)) return null;

  const sector = parseInt(postalCode.slice(0, 2), 10);
  const ranges = [
    [1, 6, 1],
    [7, 8, 2],
    [9, 10, 4],
    [11, 13, 5],
    [14, 16, 3],
    [17, 17, 6],
    [18, 19, 7],
    [20, 21, 8],
    [22, 23, 9],
    [24, 27, 10],
    [28, 30, 11],
    [31, 33, 12],
    [34, 37, 13],
    [38, 41, 14],
    [42, 45, 15],
    [46, 48, 16],
    [49, 50, 17],
    [81, 81, 17],
    [51, 52, 18],
    [53, 55, 19],
    [82, 82, 19],
    [56, 57, 20],
    [58, 59, 21],
    [60, 64, 22],
    [65, 68, 23],
    [69, 71, 24],
    [72, 73, 25],
    [77, 78, 26],
    [75, 76, 27],
    [79, 80, 28],
  ];

  const match = ranges.find(([start, end]) => sector >= start && sector <= end);
  return match ? match[2] : null;
};

const regionOf = (district) => {
  if (CCR_DISTRICTS.includes(district)) return "ccr";
  if (RCR_DISTRICTS.includes(district)) return "rcr";
  return "ocr";
};

const monthlyRepayment = (principal, monthlyRate, months) => {
  if (principal <= 0 || months <= 0) return { pi: 0, io: 0 };
  const pi =
    (principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
  return { pi, io: principal * monthlyRate };
};

const maskAmount = (amount) => {
  if (!(amount > 0)) return "S$---,---";
  let seenFirstDigit = false;
  return formatCurrency(amount)
    .split("")
    .map((char) => {
      if (!/\d/.test(char)) return char;
      if (!seenFirstDigit) {
        seenFirstDigit = true;
        return char;
      }
      return "-";
    })
    .join("");
};

const normalizeCampaignMobile = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.startsWith("65") ? digits : `65${digits}`;
};

const PropertyCampaign = () => {
  useEffect(() => {
    document.documentElement.classList.add("property-campaign-page");
    return () =>
      document.documentElement.classList.remove("property-campaign-page");
  }, []);

  const [step, setStep] = useState(1);
  const [postalCode, setPostalCode] = useState("");
  const [postalTouched, setPostalTouched] = useState(false);
  const [addressFloor, setAddressFloor] = useState("");
  const [addressUnit, setAddressUnit] = useState("");
  const [propertyType, setPropertyType] = useState("condo");
  const [product, setProduct] = useState("caveat");
  const [owners, setOwners] = useState("1");
  const [guarantor, setGuarantor] = useState("all");
  const [mortgageAmount, setMortgageAmount] = useState("0");
  const [cpfUsage, setCpfUsage] = useState("0");
  const [tenor, setTenor] = useState(PRODUCTS.caveat.defaultTenor);
  const [unlocked, setUnlocked] = useState(false);
  const [phone, setPhone] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpSession, setOtpSession] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [valuationResult, setValuationResult] = useState(null);
  const [valuationCandidates, setValuationCandidates] = useState([]);
  const [valuationLoading, setValuationLoading] = useState(false);
  const [valuationError, setValuationError] = useState("");
  const [visitorToken] = useState(() => {
    const existing = Cookies.get(LANDING_PAGE_VISITOR_COOKIE);
    if (existing) return existing;

    const generated = createVisitorToken();
    Cookies.set(LANDING_PAGE_VISITOR_COOKIE, generated, {
      expires: 365,
      path: "/",
    });
    return generated;
  });
  const saveTimeoutRef = useRef(null);

  const district = useMemo(() => districtOf(postalCode.trim()), [postalCode]);
  const region = district ? regionOf(district) : null;
  const floorUnit = getFloorUnit(addressFloor, addressUnit);
  const effectivePropertyType = valuationResult?.propertyType || propertyType;
  const valuation = valuationResult?.value || 0;
  const productDetails = PRODUCTS[product];
  const ltv = LTV_BY_TYPE[effectivePropertyType] || 0;
  const isHalfCaveat = owners === "2" && guarantor === "one";
  const mortgage = parseAmount(mortgageAmount);
  const cpf = parseAmount(cpfUsage);
  const numericTenor = Math.min(
    Math.max(1, Number(tenor) || productDetails.defaultTenor),
    productDetails.cap,
  );
  const months = product === "caveat" ? numericTenor : numericTenor * 12;
  const guarantorFactor = isHalfCaveat ? 0.5 : 1;

  const result = useMemo(() => {
    if (!valuation) {
      return {
        gross: 0,
        net: 0,
        principalForRepayment: 0,
        committedPercent: 0,
        eligiblePercent: 0,
        bufferPercent: 100,
      };
    }

    if (product === "firstlm") {
      const gross = Math.max((ltv * valuation - cpf) * guarantorFactor, 0);
      const net = Math.max(gross - mortgage, 0);
      const committedPercent = Math.min(
        100,
        ((mortgage + cpf) / valuation) * 100,
      );
      const eligiblePercent = Math.min(
        100 - committedPercent,
        (net / valuation) * 100,
      );
      return {
        gross,
        net,
        principalForRepayment: gross,
        committedPercent,
        eligiblePercent,
        bufferPercent: Math.max(0, 100 - committedPercent - eligiblePercent),
      };
    }

    const amount = Math.max(
      (ltv * valuation - mortgage - cpf) * guarantorFactor,
      0,
    );
    const committedPercent = Math.min(
      100,
      ((mortgage + cpf) / valuation) * 100,
    );
    const eligiblePercent = Math.min(
      100 - committedPercent,
      (amount / valuation) * 100,
    );

    return {
      gross: amount,
      net: amount,
      principalForRepayment: amount,
      committedPercent,
      eligiblePercent,
      bufferPercent: Math.max(0, 100 - committedPercent - eligiblePercent),
    };
  }, [valuation, product, ltv, cpf, mortgage, guarantorFactor]);

  const repayment = monthlyRepayment(
    result.principalForRepayment,
    productDetails.rate,
    months,
  );
  const postalError = postalTouched && !district;
  const normalizedPhone = normalizeCampaignMobile(phone);
  const phoneValid = /^65[3689]\d{7}$/.test(normalizedPhone);
  const showPhoneError = phoneTouched && !phoneValid;
  const hasCalculated = step === 2 && valuation > 0;
  const isEligible = result.net >= MIN_LOAN;

  const buildLeadData = (overrides = {}) => ({
    source: LANDING_PAGE_PATH,
    postal_code: postalCode,
    address_floor: addressFloor,
    address_unit: addressUnit,
    matched_address_unit: valuationResult?.addressUnit || "",
    matched_address: valuationResult?.address || "",
    district,
    region,
    selected_property_type: propertyType,
    property_type: effectivePropertyType,
    valuation_api_type: valuationResult?.apiType || "",
    valuation_candidate_count: valuationCandidates.length,
    valuation_match_method: valuationResult ? "api_property_valuation" : "",
    product,
    owners,
    guarantor,
    half_caveat: isHalfCaveat,
    mortgage_amount: mortgage,
    cpf_usage: cpf,
    tenor: numericTenor,
    tenor_unit: productDetails.unit,
    valuation,
    gross_facility: result.gross,
    net_cash_released: result.net,
    monthly_pi: repayment.pi,
    monthly_interest_only: repayment.io,
    phone: normalizedPhone || phone,
    otp_sent: otpSent,
    otp_verified: unlocked,
    unlocked,
    lead_submitted: leadSubmitted,
    ...overrides,
  });

  const saveLeadSnapshot = async (overrides = {}) => {
    const { lead_data: leadDataOverrides, ...payloadOverrides } = overrides;
    const payload = {
      visitor_token: visitorToken,
      landing_page_path: LANDING_PAGE_PATH,
      phone_number: normalizedPhone || phone,
      lead_data: buildLeadData(leadDataOverrides),
      ...payloadOverrides,
    };

    try {
      await fetch(LANDING_PAGE_LEAD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error("Failed to save property campaign lead", error);
    }
  };

  useEffect(() => {
    Cookies.set(LANDING_PAGE_SOURCE_COOKIE, LANDING_PAGE_PATH, {
      expires: 30,
      path: "/",
    });
    saveLeadSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveLeadSnapshot(), 1200);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    postalCode,
    addressFloor,
    addressUnit,
    propertyType,
    effectivePropertyType,
    valuationResult,
    valuationCandidates.length,
    product,
    owners,
    guarantor,
    mortgageAmount,
    cpfUsage,
    tenor,
    phone,
    normalizedPhone,
    otpSent,
    unlocked,
    leadSubmitted,
  ]);

  useEffect(() => {
    if (owners === "1" && guarantor !== "all") {
      setGuarantor("all");
    }
  }, [owners, guarantor]);

  useEffect(() => {
    setTenor(PRODUCTS[product].defaultTenor);
  }, [product]);

  const resetValuation = () => {
    setValuationResult(null);
    setValuationCandidates([]);
    setValuationError("");
    setUnlocked(false);
    setLeadSubmitted(false);
    setOtpSent(false);
    setOtpSession("");
    setOtpCode("");
    setOtpError("");
  };

  const handleStepOne = async () => {
    setPostalTouched(true);
    if (!district) return;
    setValuationLoading(true);
    setValuationError("");

    try {
      const candidates = await getValuationCandidates(postalCode.trim());
      setValuationCandidates(candidates);
      const narrowed = narrowValuationCandidates({
        candidates,
        floorUnit,
        selectedPropertyType: propertyType,
      });

      if (!narrowed.result?.value) {
        const selectedTypeName = TYPE_NAME[propertyType] || "property";
        const messageByReason = {
          none_for_type: `We could not find a ${selectedTypeName} valuation for this postal code. Try another property type or check the postal code.`,
          needs_unit: `We found ${narrowed.typedCount} possible ${selectedTypeName} valuations. Enter floor and unit so we can identify the correct property.`,
          no_unit_match:
            "We could not match that floor/unit to the valuation records for this postal code. Please check the unit number.",
          multiple_unit_matches:
            "More than one valuation matched that unit. Please contact SmartLend so a specialist can verify it.",
        };

        setValuationResult(null);
        setValuationError(
          messageByReason[narrowed.reason] ||
            "We could not retrieve a reliable valuation for this property.",
        );
        return;
      }

      console.log("[PropertyCampaign][Valuation] matched property", {
        postalCode: postalCode.trim(),
        selectedPropertyType: propertyType,
        normalizedFloorUnit: floorUnit,
        matchReason: narrowed.reason,
        totalCandidateCount: narrowed.count,
        typedCandidateCount: narrowed.typedCount,
        matchedResult: narrowed.result,
        rawMatchedProperty: narrowed.result.raw,
      });

      setValuationResult(narrowed.result);
      setStep(2);
      window.setTimeout(() => {
        document.getElementById("pc-calculator-result")?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 80);
    } catch (error) {
      console.error("Failed to retrieve property valuation", error);
      setValuationResult(null);
      setValuationError(
        "We could not retrieve the property valuation right now. Please try again.",
      );
    } finally {
      setValuationLoading(false);
    }
  };

  const handleSendOtp = async () => {
    setPhoneTouched(true);
    if (!phoneValid) return;

    setOtpBusy(true);
    setOtpError("");

    try {
      const response = await fetch(PROPERTY_CAMPAIGN_OTP_SEND_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile_no: normalizedPhone }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.message || "Unable to send OTP.");
      }

      setOtpSession(data?.data?.session || "");
      setOtpSent(true);
      setOtpCode("");
      await saveLeadSnapshot({
        lead_data: {
          otp_sent: true,
          otp_verified: false,
          unlocked: false,
        },
      });
    } catch (error) {
      console.error("Failed to send property campaign OTP", error);
      setOtpError(error.message || "Unable to send OTP. Please try again.");
    } finally {
      setOtpBusy(false);
    }
  };

  const handleVerifyOtp = async () => {
    setPhoneTouched(true);
    if (!phoneValid || !otpSession || otpCode.length !== 4) return;

    setOtpBusy(true);
    setOtpError("");

    try {
      const verifiedAt = new Date().toISOString();
      const response = await fetch(PROPERTY_CAMPAIGN_OTP_VERIFY_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          visitor_token: visitorToken,
          mobile_no: normalizedPhone,
          session: otpSession,
          otp: otpCode,
          lead_data: buildLeadData({
            otp_sent: true,
            otp_verified: true,
            otp_verified_at: verifiedAt,
            unlocked: true,
          }),
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.message || "The OTP is incorrect.");
      }

      setUnlocked(true);
      await saveLeadSnapshot({
        phone_number: normalizedPhone,
        lead_data: {
          otp_sent: true,
          otp_verified: true,
          otp_verified_at: verifiedAt,
          unlocked: true,
        },
      });
    } catch (error) {
      console.error("Failed to verify property campaign OTP", error);
      setOtpError(
        error.message ||
          "The OTP is incorrect. Try again or request a new code.",
      );
    } finally {
      setOtpBusy(false);
    }
  };

  const handleApply = async () => {
    const campaignPayload = buildLeadData({
      phone: normalizedPhone || phone,
      created_at: new Date().toISOString(),
    });

    Cookies.set(PROPERTY_CAMPAIGN_COOKIE, JSON.stringify(campaignPayload), {
      expires: 30,
      path: "/",
    });
    Cookies.set(LANDING_PAGE_SOURCE_COOKIE, LANDING_PAGE_PATH, {
      expires: 30,
      path: "/",
    });
    setLeadSubmitted(true);
    await saveLeadSnapshot({
      phone_number: normalizedPhone || phone,
      lead_data: {
        lead_submitted: true,
      },
    });

    const isAuthenticated = Cookies.get("token");
    const userAccountType = Cookies.get("user_account_type");

    if (isAuthenticated && userAccountType === "borrower") {
      window.location.href = "/b-instant-prop-term-loan";
      return;
    }

    window.location.href = "/auth/signin";
  };

  const displayAmount = unlocked
    ? formatCurrency(result.net)
    : maskAmount(result.net);
  const resultLabel =
    product === "firstlm"
      ? "Net cash you could unlock"
      : "You could qualify for up to";
  const resultNote = !hasCalculated
    ? "Search your postal code to see your indicative valuation, eligible amount and monthly instalments."
    : !unlocked
      ? "Estimated from your property equity. Verify your mobile number with OTP to reveal the full figure and save your result."
      : isEligible
        ? product === "firstlm"
          ? `After a ${formatCurrency(result.gross)} facility redeems your outstanding loan. Indicative, subject to approval.`
          : "Indicative caveat backed loan amount, secured against your property equity."
        : "This may be below the usual S$50k minimum, but a specialist can still review possible structures for free.";

  return (
    <main className="property-campaign">
      <svg
        width="0"
        height="0"
        aria-hidden="true"
        focusable="false"
        style={{ position: "absolute" }}
      >
        <defs>
          <filter
            id="property-campaign-white-wordmark"
            x="0"
            y="0"
            width="100%"
            height="100%"
            primitiveUnits="objectBoundingBox"
            colorInterpolationFilters="sRGB"
          >
            <feFlood
              x="0.27"
              y="0"
              width="0.73"
              height="1"
              floodColor="white"
              result="wordmark"
            />
            <feComposite in="wordmark" in2="SourceGraphic" operator="in" />
            <feComponentTransfer>
              <feFuncA type="linear" slope="2.01" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode in="SourceGraphic" />
              <feMergeNode />
            </feMerge>
          </filter>
        </defs>
      </svg>
      <nav aria-label="Property campaign navigation">
        <a className="pcd-logo" href="/home" aria-label="SmartLend home">
          <img
            className="pcd-lg-m"
            src={campaignLogoMobile}
            alt="SmartLend"
            width="132"
            height="19"
          />
          <img
            className="pcd-lg-d"
            src={campaignLogoDesktop}
            alt=""
            width="152"
            height="22"
          />
        </a>
        <div className="pcd-links">
          <a href="#calculator">Calculator</a>
          <a href="#why-smartlend">Why SmartLend</a>
          <a href="#compare">Compare</a>
        </div>
        <a className="pcd-btn" href="#calculator">
          Check eligibility
        </a>
      </nav>

      <header className="pcd-hero" id="top">
        <div className="pcd-wrap">
          <span className="pcd-tag">
            Caveat backed loans and 1st legal mortgages
          </span>
          <h1>
            Your property is worth more than it owes.{" "}
            <em>Unlock the difference.</em>
          </h1>
          <p className="pcd-tdsr">No TDSR assessment.</p>
          <p className="pcd-lead">
            Get an indicative property value and see how much you could unlock
            through multiple financial institutions and private lenders in about
            60 seconds.
          </p>
          <div className="pcd-cta">
            <a className="pcd-btn pcd-btn-amber" href="#calculator">
              Check how much I can unlock
            </a>
            <a className="pcd-btn pcd-btn-ghost" href="#why-smartlend">
              See why it’s different
            </a>
          </div>
          <div className="pcd-chips">
            <span className="pcd-chip">24–48 hr approval</span>
            <span className="pcd-chip pcd-g">S$0 SmartLend broker fee</span>
          </div>
          <p className="pcd-fine">
            Free. No obligation. Indicative only. Private property. Lender fees
            disclosed before signing.
          </p>

          <div className="pcd-eg" aria-label="Example eligibility">
            <div className="pcd-head">
              <h3>Indicative eligibility</h3>
              <span className="pcd-badge">60-sec check</span>
            </div>
            <small>
              Illustrative example. Final terms are subject to valuation and
              lender approval.
            </small>
            <div className="pcd-row">
              <span>Postal code</span>
              <b>428765 · D15</b>
            </div>
            <div className="pcd-row">
              <span>Property value</span>
              <b>S$2,400,000</b>
            </div>
            <div className="pcd-row">
              <span>Outstanding loan + CPF</span>
              <b>– S$1,100,000</b>
            </div>
            <div className="pcd-res">
              <div className="pcd-l">Indicative caveat backed loan</div>
              <div className="pcd-big">S$940,000</div>
              <div className="pcd-r">
                <span>Max LTV</span>
                <b className="pcd-y">Up to 85%</b>
              </div>
              <div className="pcd-r">
                <span>Est. interest-only instalment</span>
                <b>S$14,100 / mth</b>
              </div>
              <div className="pcd-r">
                <span>SmartLend broker fee</span>
                <b className="pcd-g">S$0</b>
              </div>
            </div>
          </div>

          <div className="pcd-stats">
            <div className="pcd-stat">
              <b>S$0 broker fee</b>
              <span>
                SmartLend charges borrowers nothing. Lender fees are disclosed
                upfront.
              </span>
            </div>
            <div className="pcd-stat">
              <b>Up to 90% LTV</b>
              <span>90% landed, 85% condo, 75% commercial and industrial.</span>
            </div>
            <div className="pcd-stat">
              <b>24–48 hr approval</b>
              <span>
                Funding is typically within a week after approval and legal
                steps.
              </span>
            </div>
            <div className="pcd-stat">
              <b>Equity based</b>
              <span>
                Eligibility is driven primarily by private property equity.
              </span>
            </div>
          </div>
        </div>
      </header>

      <section className="pc-calc-section" id="calculator">
        <div className="pcd-wrap">
          <div className="pc-calc-section-head">
            <div className="pcd-kicker">Instant eligibility</div>
            <h2>Know your number before you speak to anyone.</h2>
            <p className="pcd-sub">
              Search your postal code for an indicative valuation, then add your
              outstanding loan and CPF usage to see your eligible amount with
              estimated monthly repayments.
            </p>
          </div>

          <div className="pcd-tabs" aria-label="Progress">
            <div
              className={`pcd-tab ${step === 1 ? "pcd-on" : "pc-step-done"}`}
            >
              <i>1</i> Instant valuation
            </div>

            <div className={`pcd-tab ${step === 2 ? "pcd-on" : ""}`}>
              <i>2</i> Eligible amount and instalment
            </div>
          </div>

          <div className="pc-calculator">
            <div className="pcd-card pc-calc-card">
              {step === 1 ? (
                <>
                  <div className="pcd-step">Step 1 - Instant valuation</div>
                  <h3 className="pc-question">
                    Where’s your property located?
                  </h3>
                  <div className="pc-searchbar">
                    <label htmlFor="pc-postal-code">Postal code</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      id="pc-postal-code"
                      autoComplete="postal-code"
                      placeholder="Enter your Singapore postal code"
                      value={postalCode}
                      onBlur={() => setPostalTouched(true)}
                      onChange={(event) => {
                        resetValuation();
                        setPostalCode(event.target.value.replace(/\D/g, ""));
                      }}
                    />
                    {district ? (
                      <span className="pc-found">
                        D{district} - {REGION_NAME[region]}
                      </span>
                    ) : null}
                  </div>
                  {postalError ? (
                    <div className="pc-error">
                      Please enter a valid 6-digit Singapore postal code.
                    </div>
                  ) : null}
                  <div className="pcd-note">
                    Private property only. Estimates are intentionally prudent
                    and may differ from a formal valuation.
                  </div>
                  <div className="pcd-two">
                    <label className="pc-field">
                      <div className="pc-field-label">Floor</div>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="05"
                        value={addressFloor}
                        onChange={(event) => {
                          resetValuation();
                          setAddressFloor(event.target.value);
                        }}
                      />
                    </label>
                    <label className="pc-field">
                      <div className="pc-field-label">Unit</div>
                      <input
                        type="text"
                        placeholder="12"
                        value={addressUnit}
                        onChange={(event) => {
                          resetValuation();
                          setAddressUnit(event.target.value);
                        }}
                      />
                    </label>
                  </div>
                  <div className="pcd-note">
                    Unit details help us pick the correct valuation when a
                    postal code has multiple matching records.
                  </div>

                  <div className="pc-field-label">Property type</div>
                  <div
                    className="pcd-types"
                    role="group"
                    aria-label="Property type"
                  >
                    {propertyTabs.map((item) => (
                      <button
                        className={`pcd-type ${
                          propertyType === item.key ? "pcd-type-active" : ""
                        }`}
                        key={item.key}
                        aria-pressed={propertyType === item.key}
                        type="button"
                        onClick={() => {
                          resetValuation();
                          setPropertyType(item.key);
                        }}
                      >
                        <i>{item.icon}</i>
                        {item.label}
                      </button>
                    ))}
                  </div>
                  <div className="pcd-ltv" aria-live="polite">
                    Loan up to {Math.round(ltv * 100)}% of property value for{" "}
                    {TYPE_NAME[propertyType]}
                  </div>
                  <div className="pc-form-nav pc-valuation-action">
                    <span className="pcd-note">
                      Takes about 20 seconds. Free. S$0 SmartLend broker fee.
                    </span>
                    <button
                      className="pcd-btn pcd-btn-blue"
                      type="button"
                      onClick={handleStepOne}
                      disabled={valuationLoading}
                    >
                      {valuationLoading
                        ? "Checking valuation..."
                        : "Get my instant valuation"}
                    </button>
                  </div>
                  {valuationError ? (
                    <div className="pc-error pc-error-panel">
                      {valuationError}
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <h3 className="pc-pane-title">
                    Step 2 - Your financing needs
                  </h3>
                  <p className="pc-pane-sub">
                    Choose a facility, then tell us what is currently owing on
                    the property.
                  </p>

                  <div className="pc-field-label">Facility type</div>
                  <div className="pcd-types pcd-types-two">
                    {productTabs.map((item) => (
                      <button
                        className={`pcd-type ${
                          product === item.key ? "pcd-type-active" : ""
                        }`}
                        key={item.key}
                        aria-pressed={product === item.key}
                        type="button"
                        onClick={() => setProduct(item.key)}
                      >
                        <i>{item.icon}</i>
                        {item.label}
                        <small>{item.note}</small>
                      </button>
                    ))}
                  </div>
                  <div className="pc-what-links">
                    <a href="#faq-caveat">What is a caveat backed loan?</a>
                    <span>-</span>
                    <a href="#faq-lm">What is a 1st legal mortgage?</a>
                  </div>

                  <div className="pc-fields">
                    <label className="pc-field">
                      <div className="pc-field-label">Owners on property</div>
                      <select
                        value={owners}
                        onChange={(event) => setOwners(event.target.value)}
                      >
                        <option value="1">1 - sole owner</option>
                        <option value="2">2 or more - joint owners</option>
                      </select>
                    </label>
                    <label className="pc-field">
                      <div className="pc-field-label">Personal guarantor</div>
                      <select
                        value={guarantor}
                        disabled={owners === "1"}
                        onChange={(event) => setGuarantor(event.target.value)}
                      >
                        <option value="all">All owners</option>
                        <option value="one">Only 1 owner</option>
                      </select>
                      <div className="pcd-note">
                        {owners === "1"
                          ? "Sole owner signs as guarantor."
                          : "If only one owner signs, half caveat treatment may apply."}
                      </div>
                    </label>
                    {isHalfCaveat ? (
                      <div className="pc-field-wide pc-warn">
                        Only one joint owner is signing, so the indicative
                        eligible amount is halved.
                      </div>
                    ) : null}
                    <label className="pc-field">
                      <div className="pc-field-label">
                        Outstanding loan amount (S$)
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="10000"
                        value={mortgageAmount}
                        onChange={(event) =>
                          setMortgageAmount(event.target.value)
                        }
                      />
                      <div className="pcd-note">
                        Current balance on this property. Enter 0 if fully paid.
                      </div>
                    </label>
                    <label className="pc-field">
                      <div className="pc-field-label">
                        Est. CPF usage incl. accrued interest (S$)
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="10000"
                        value={cpfUsage}
                        onChange={(event) => setCpfUsage(event.target.value)}
                      />
                      <div className="pcd-note">
                        Check under My dashboards ➔ Property on the CPF website.
                      </div>
                    </label>
                    <label className="pc-field pc-field-wide">
                      <div className="pc-field-label">
                        {productDetails.tenorLabel}
                      </div>
                      <input
                        type="number"
                        min="1"
                        max={productDetails.cap}
                        step="1"
                        value={tenor}
                        onBlur={() => setTenor(numericTenor)}
                        onChange={(event) => setTenor(event.target.value)}
                      />
                      <div className="pcd-note">
                        {productDetails.tenorHint} Indicative rate from{" "}
                        {productDetails.rateLabel}.
                      </div>
                    </label>
                  </div>

                  <div className="pc-form-nav">
                    <button
                      className="pcd-btn pc-button-light"
                      type="button"
                      onClick={() => setStep(1)}
                    >
                      Back
                    </button>
                    <a
                      className="pcd-btn pcd-btn-blue"
                      href="#pc-calculator-result"
                    >
                      Show my amount and instalments
                    </a>
                  </div>
                </>
              )}
            </div>

            <aside
              className="pcd-out"
              id="pc-calculator-result"
              aria-live="polite"
            >
              <div className="pcd-top">
                <span>
                  Indicative valuation
                  <b>{valuation ? formatCurrency(valuation) : "-"}</b>
                </span>
                <span style={{ textAlign: "right" }}>
                  District
                  <b>
                    {district ? `D${district} - ${REGION_NAME[region]}` : "-"}
                  </b>
                </span>
              </div>
              <div className="pcd-l">
                {hasCalculated ? resultLabel : "Your result"}
              </div>
              <div className="pcd-big">
                {hasCalculated ? displayAmount : "S$ - - -"}
              </div>
              <p className="pc-side-note">{resultNote}</p>

              {hasCalculated && !unlocked ? (
                <div className="pc-unlock">
                  <label htmlFor="pc-phone">
                    Verify mobile to reveal your amount
                  </label>
                  <input
                    id="pc-phone"
                    type="tel"
                    placeholder="+65 9XXX XXXX"
                    value={phone}
                    onBlur={() => setPhoneTouched(true)}
                    onChange={(event) => {
                      setPhone(event.target.value);
                      setUnlocked(false);
                      setOtpSent(false);
                      setOtpSession("");
                      setOtpCode("");
                      setOtpError("");
                    }}
                  />
                  {showPhoneError ? (
                    <div className="pc-error">
                      Please enter a valid Singapore mobile number.
                    </div>
                  ) : null}
                  {otpSent ? (
                    <>
                      <div className="pc-otp-row">
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="4-digit OTP"
                          value={otpCode}
                          onChange={(event) =>
                            setOtpCode(event.target.value.replace(/\D/g, ""))
                          }
                        />
                        <button
                          className="pcd-btn pcd-btn-amber"
                          type="button"
                          onClick={handleVerifyOtp}
                          disabled={otpBusy || otpCode.length !== 4}
                        >
                          {otpBusy ? "Verifying..." : "Verify OTP"}
                        </button>
                      </div>
                      <button
                        className="pc-resend-link"
                        type="button"
                        onClick={handleSendOtp}
                        disabled={otpBusy}
                      >
                        Resend OTP
                      </button>
                    </>
                  ) : (
                    <button
                      className="pcd-btn pcd-btn-amber"
                      type="button"
                      onClick={handleSendOtp}
                      disabled={otpBusy}
                    >
                      {otpBusy ? "Sending OTP..." : "Send OTP to reveal amount"}
                    </button>
                  )}
                  {otpError ? <div className="pc-error">{otpError}</div> : null}
                </div>
              ) : null}

              <div className="pc-mini-bars">
                <div className="pcd-r">
                  <span>Indicative property value</span>
                  <b>{valuation ? formatCurrency(valuation) : "-"}</b>
                </div>
                {product === "firstlm" && hasCalculated ? (
                  <div className="pcd-r">
                    <span>Gross facility</span>
                    <b>
                      {unlocked
                        ? formatCurrency(result.gross)
                        : maskAmount(result.gross)}
                    </b>
                  </div>
                ) : null}
                <div className="pcd-r">
                  <span>Outstanding loan</span>
                  <b>
                    {mortgage > 0 ? `- ${formatCurrency(mortgage)}` : "S$0"}
                  </b>
                </div>
                <div className="pcd-r">
                  <span>Est. CPF usage</span>
                  <b>{cpf > 0 ? `- ${formatCurrency(cpf)}` : "S$0"}</b>
                </div>
                <div className="pcd-r">
                  <span>Maximum LTV</span>
                  <b>
                    {Math.round(ltv * 100)}%{isHalfCaveat ? " x 50%" : ""}
                  </b>
                </div>
              </div>

              {hasCalculated && unlocked ? (
                <>
                  <div
                    className="pc-breakdown"
                    aria-label="Property value breakdown"
                  >
                    <span
                      className="pc-bar-committed"
                      style={{ width: `${result.committedPercent}%` }}
                    />
                    <span
                      className="pc-bar-eligible"
                      style={{ width: `${result.eligiblePercent}%` }}
                    />
                    <span
                      className="pc-bar-buffer"
                      style={{ width: `${result.bufferPercent}%` }}
                    />
                  </div>
                  <div className="pc-legend">
                    <span>
                      <i
                        className="pc-swatch"
                        style={{ background: "#93938c" }}
                      />
                      Loan + CPF
                    </span>
                    <span>
                      <i
                        className="pc-swatch"
                        style={{ background: "#5fa8f5" }}
                      />
                      Eligible
                    </span>
                    <span>
                      <i
                        className="pc-swatch"
                        style={{ background: "rgba(255,255,255,.28)" }}
                      />
                      Buffer
                    </span>
                  </div>
                </>
              ) : null}

              <div className="pcd-tiles">
                <div className="pcd-tile">
                  <small>P+I monthly</small>
                  <b>
                    {hasCalculated && unlocked
                      ? formatCurrency(repayment.pi)
                      : "S$--,---"}
                  </b>
                  <span>
                    {hasCalculated
                      ? `${productDetails.rateLabel} - ${numericTenor} ${productDetails.unit}`
                      : "estimated monthly repayment"}
                  </span>
                </div>
                <div className="pcd-tile">
                  <small>Interest-only monthly</small>
                  <b>
                    {hasCalculated && unlocked
                      ? formatCurrency(repayment.io)
                      : "S$-,---"}
                  </b>
                  <span>Principal due at the end of term</span>
                </div>
              </div>

              {hasCalculated && unlocked ? (
                <>
                  <button
                    className="pcd-btn pcd-btn-amber"
                    type="button"
                    onClick={handleApply}
                    style={{ width: "100%" }}
                  >
                    Lock in this quote - get a call back
                  </button>
                  {leadSubmitted ? (
                    <div className="pcd-note2" style={{ marginTop: 12 }}>
                      Thanks. A SmartLend specialist will follow up with you.
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="pcd-note2">
                  No documents are required for the indicative check. A
                  specialist confirms the quote with a formal valuation.
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>

      <section id="why-smartlend" style={{ background: "var(--card)" }}>
        <div className="pcd-wrap">
          <p className="pcd-kicker">Why apply through SmartLend</p>
          <h2>Singpass in. Offers out. Just 3 steps.</h2>
          <p className="pcd-sub">
            With Singpass and Credit Bureau integration, applying takes minutes.
            Then our concierge helps compare offers across rate, fees, timeline,
            tenor and net cash released.
          </p>
          <div className="pcd-stack">
            <div className="pcd-card">
              <h3>Application in 3 steps</h3>
              <ol className="pcd-steps">
                <li>
                  <span className="pcd-n">1</span>
                  <div>
                    <b>Scan your Singpass</b>
                    <span>
                      Property and CPF details can be retrieved accurately, so
                      there is less manual digging.
                    </span>
                  </div>
                </li>
                <li>
                  <span className="pcd-n">2</span>
                  <div>
                    <b>Get your Credit Bureau report on us</b>
                    <span>
                      Your Credit Bureau Singapore report (usually S$8) is on us
                      when lenders need it. You’ll never be asked to pay for it.
                    </span>
                  </div>
                </li>
                <li>
                  <span className="pcd-n">3</span>
                  <div>
                    <b>Compare offers side by side</b>
                    <span>
                      One application reaches suitable participating lenders;
                      your concierge lines up the terms.
                    </span>
                  </div>
                </li>
              </ol>
              <a
                className="pcd-btn pcd-btn-blue"
                href="#calculator"
                style={{ marginTop: "0" }}
              >
                Start with my instant valuation
              </a>
            </div>
            <div className="pcd-card">
              <h3>
                A concierge on your side — not a salesperson on commission.
              </h3>
              <div className="pcd-pts">
                <div className="pcd-pt">
                  <div className="pcd-ic">S$0</div>
                  <div>
                    <h4>No SmartLend broker fee</h4>
                    <p>
                      Lenders pay us for introductions. SmartLend does not take
                      a broker fee from your loan proceeds.
                    </p>
                  </div>
                </div>
                <div className="pcd-pt">
                  <div className="pcd-ic">Fit</div>
                  <div>
                    <h4>Compare the total package</h4>
                    <p>
                      Net proceeds, lender fees, speed, tenor and exit terms
                      matter as much as the headline rate.
                    </p>
                  </div>
                </div>
                <div className="pcd-pt">
                  <div className="pcd-ic">1</div>
                  <div>
                    <h4>One concierge. Zero spam.</h4>
                    <p>
                      Your number stays yours. No lender call barrage, no broker
                      follow-ups - you deal with SmartLend while lenders compete
                      behind the scenes for the best fit.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="pcd-wrap">
          <span id="compare"></span>
          <p className="pcd-kicker">Marketplace experience</p>
          <h2>Offers side by side, on the terms that actually matter.</h2>
          <p className="pcd-sub">
            Your concierge presents matched offers across the dimensions that
            actually matter, not just an anonymous rate.
          </p>
          <div className="pcd-card" style={{ padding: "16px" }}>
            <div className="pcd-head">
              <h3>Illustrative matched offers</h3>
              <span className="pcd-badge">Illustrative</span>
            </div>
            <p className="pcd-note" style={{ margin: "-8px 0 12px" }}>
              Example presentation only. Actual terms depend on underwriting and
              legal structure.
            </p>
            <div
              className="pcd-tbl"
              tabIndex="0"
              role="region"
              aria-label="Offer comparison table"
            >
              <table>
                <thead>
                  <tr>
                    <th>Compare</th>
                    <th>Offer A</th>
                    <th>Offer B</th>
                    <th>Offer C</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Net cash released</td>
                    <td>S$580k</td>
                    <td className="pcd-b">S$610k</td>
                    <td>S$600k</td>
                  </tr>
                  <tr>
                    <td>Indicative rate</td>
                    <td>1.5% / mth</td>
                    <td>1.6% / mth</td>
                    <td style={{ color: "var(--green)", fontWeight: "800" }}>
                      1.4% / mth
                    </td>
                  </tr>
                  <tr>
                    <td>Lender / facility fees</td>
                    <td>S$8k</td>
                    <td className="pcd-b">S$4k</td>
                    <td>S$12k</td>
                  </tr>
                  <tr>
                    <td>Funding timeline</td>
                    <td style={{ color: "var(--green)", fontWeight: "800" }}>
                      24 hrs*
                    </td>
                    <td>48 hrs*</td>
                    <td>48 hrs*</td>
                  </tr>
                  <tr>
                    <td>Tenor</td>
                    <td>6 months</td>
                    <td className="pcd-b">12 months</td>
                    <td>6 months</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="pcd-swipe">Swipe sideways to see all offers.</p>
            <p className="pcd-note">
              * After approval, signing and legal completion. The best-fit offer
              is not always the lowest headline rate.
            </p>
          </div>
        </div>
      </section>

      <section style={{ background: "var(--card)" }}>
        <div className="pcd-wrap">
          <p className="pcd-kicker">Caveat backed loan vs 1st legal mortgage</p>
          <h2>Two ways to unlock equity. Pick your speed.</h2>
          <p className="pcd-sub">
            Both are secured against private property. The right one depends on
            how much you need, how quickly you need it, and how long you need to
            repay.
          </p>
          <div className="pcd-stack">
            <div className="pcd-cmp pcd-hl">
              <h3>Caveat Backed Loan</h3>
              <div className="pcd-kv">
                <span>Security</span>
                <b>Caveat (2nd charge)</b>
              </div>
              <div className="pcd-kv">
                <span>Existing mortgage</span>
                <b>Can remain in place</b>
              </div>
              <div className="pcd-kv">
                <span>Max LTV</span>
                <b>75–90% by property type</b>
              </div>
              <div className="pcd-kv">
                <span>Indicative rate</span>
                <b>From 1.5% / month</b>
              </div>
              <div className="pcd-kv">
                <span>Typical timeline</span>
                <b>Approval 24–48 hrs</b>
              </div>
              <div className="pcd-kv">
                <span>Tenor</span>
                <b>Capped at 1 year</b>
              </div>
            </div>
            <div className="pcd-cmp">
              <h3>1st Legal Mortgage</h3>
              <div className="pcd-kv">
                <span>Security</span>
                <b>1st charge mortgage</b>
              </div>
              <div className="pcd-kv">
                <span>Existing mortgage</span>
                <b>Redeemed by the facility</b>
              </div>
              <div className="pcd-kv">
                <span>Max LTV</span>
                <b>75–90% by property type</b>
              </div>
              <div className="pcd-kv">
                <span>Indicative rate</span>
                <b>From 6% / year</b>
              </div>
              <div className="pcd-kv">
                <span>Typical timeline</span>
                <b>1–3 weeks</b>
              </div>
              <div className="pcd-kv">
                <span>Tenor</span>
                <b>Up to 20 years</b>
              </div>
            </div>
            <div className="pcd-mini">
              <div>
                <i>S$0</i>
                <b>No broker fee</b>
                <span>
                  SmartLend charges borrowers no broker fee, upfront fee or
                  success fee.
                </span>
              </div>
              <div>
                <i>Fast</i>
                <b>Speed over paperwork</b>
                <span>
                  Built for time-sensitive funding where property equity does
                  the heavy lifting.
                </span>
              </div>
              <div>
                <i>LTV</i>
                <b>No TDSR assessment</b>
                <span>
                  Eligibility is driven primarily by property equity, subject to
                  lender approval.
                </span>
              </div>
              <div>
                <i>Fit</i>
                <b>Compare offers</b>
                <span>
                  Compare net cash released, fees, funding timeline and exit
                  terms to identify the best-fit offer.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="pcd-wrap">
          <p className="pcd-kicker">Eligibility</p>
          <h2>Simple, equity-based qualification.</h2>
          <p className="pcd-sub">
            A quick check before you invest time in a full application.
          </p>
          <div className="pcd-stack">
            <div className="pcd-card">
              <h3>You’re likely eligible if</h3>
              <ul className="pcd-li">
                <li>
                  You fully or partly own a private property in Singapore —
                  condo, landed, commercial or industrial.
                </li>
                <li>
                  There is usable equity after outstanding loan and CPF usage.
                </li>
                <li>
                  You need S$50,000 or more and have a clear repayment or exit
                  plan.
                </li>
              </ul>
            </div>
            <div className="pcd-card">
              <h3>Probably not suitable if</h3>
              <ul className="pcd-li pcd-no">
                <li>
                  The property is an HDB flat; caveats cannot be lodged for
                  private lending.
                </li>
                <li>
                  Existing loan and CPF charge already use most of the available
                  LTV.
                </li>
                <li>
                  You’re looking for unsecured financing instead of a secured
                  property-backed facility.
                </li>
              </ul>
            </div>
            <div className="pcd-dark">
              <p className="pcd-kicker">SmartLend marketplace</p>
              <h2>1 application. Every suitable lender. Best offer wins.</h2>
              <p>
                One application puts participating financial institutions and
                private lenders in competition for your case. Because lenders
                pay us for the introduction, our SmartLend broker fee to you is
                S$0.
              </p>
              <div className="pcd-pills">
                <div>
                  <b>Net cash released</b>
                  <span>The money that actually reaches you.</span>
                </div>
                <div>
                  <b>Rate and fees</b>
                  <span>Headline rate plus every lender fee.</span>
                </div>
                <div>
                  <b>Funding timeline</b>
                  <span>How fast the funds can land.</span>
                </div>
                <div>
                  <b>Tenor and exit terms</b>
                  <span>
                    Term length, early redemption and repayment options.
                  </span>
                </div>
              </div>
              <a className="pcd-btn pcd-btn-amber" href="#calculator">
                Start my application
              </a>
            </div>
          </div>
        </div>
      </section>

      <section style={{ background: "var(--card)" }}>
        <div className="pcd-wrap">
          <span id="faq"></span>
          <p className="pcd-kicker">Questions, answered</p>
          <h2>Remove hesitation before you commit.</h2>
          <p className="pcd-sub">
            Short answers upfront. A specialist can walk you through the details
            on a free call.
          </p>
          <details id="faq-caveat">
            <summary>What is a caveat backed loan?</summary>
            <p>
              A short-term loan secured by lodging a caveat as a second charge
              on your private property while your existing mortgage stays
              untouched.
            </p>
          </details>
          <details id="faq-lm">
            <summary>What is a 1st legal mortgage?</summary>
            <p>
              A facility where the lender takes first charge over your property
              and your existing mortgage is redeemed from the proceeds.
            </p>
          </details>
          <details>
            <summary>How is the instant valuation calculated?</summary>
            <p>
              The postal code identifies the district, then the page applies
              prudent estimated property type benchmarks. A formal valuation is
              still required for final terms.
            </p>
          </details>
          <details>
            <summary>Why does CPF usage matter?</summary>
            <p>
              CPF monies used for the property, plus accrued interest, rank
              ahead of new lending and reduce usable equity.
            </p>
          </details>
          <details>
            <summary>Does SmartLend charge a broker fee?</summary>
            <p>
              No. SmartLend charges borrowers S$0 broker fee. Lender processing
              and legal fees may apply and are disclosed before signing.
            </p>
          </details>
          <details>
            <summary>Can a company-owned property apply?</summary>
            <p>
              Yes. Properties held by Singapore-registered companies can be
              considered, usually with director personal guarantees.
            </p>
          </details>
        </div>
      </section>

      <section className="pcd-final">
        <div className="pcd-wrap">
          <div className="pcd-dark">
            <h2>
              Your property does the work. Our fee? <span>S$0.</span>
            </h2>
            <p>
              Search your postal code, see your eligible amount and monthly
              instalment, then let SmartLend line up suitable lender offers.
            </p>
            <a className="pcd-btn pcd-btn-amber" href="#calculator">
              Get my instant valuation
            </a>
          </div>
        </div>
      </section>

      <footer className="pcd-ft">
        <div className="pcd-wrap">
          <div>
            <a className="pcd-ft-logo" href="/home" aria-label="SmartLend home">
              <img
                className="pcd-lg-m"
                src={campaignLogoMobile}
                alt="SmartLend"
                width="132"
                height="19"
              />
              <img
                className="pcd-lg-d"
                src={campaignLogoDesktop}
                alt=""
                width="152"
                height="22"
              />
            </a>
            <div className="pcd-ft-links">
              <a href="#calculator">Eligibility check</a>
              <a href="#why-smartlend">Why SmartLend</a>
              <a href="#compare">Compare offers</a>
              <a href="#faq">FAQ</a>
            </div>
          </div>
          <p className="pcd-ft-t">
            SmartLend is a loan marketplace and does not itself lend. SmartLend
            charges borrowers no broker fee; it is remunerated by participating
            lenders. All valuations, eligible amounts and instalments shown are
            indicative estimates only and do not constitute an offer of
            financing. Final terms are subject to formal valuation, lender
            approval and legal documentation. Lender processing and legal fees
            may apply and are disclosed before signing. Property-backed loans
            are secured against your property; ensure you can meet repayments
            before borrowing.
          </p>
        </div>
      </footer>
    </main>
  );
};

export default PropertyCampaign;
